/**
 * Pure RTCStats reading for the guest sampler.
 *
 * One `getStats()` report goes in; the raw, cumulative values the sampler differences come
 * out. Nothing here keeps state or knows about intervals, baselines or capabilities: it is
 * the one place that knows which inbound-rtp / candidate-pair keys exist and what shape they
 * take, so the sampler can stay about arithmetic.
 *
 * Fields are only set when the report carries a finite value. Absence is preserved as
 * `undefined` so the sampler can omit rather than zero-fill on engines that do not report a
 * stat (Safari has no framesReceived, no freezeCount and no jitter-buffer counters).
 */

import { RtcSelectedPathReader } from './measurement/RtcSelectedPathReader';
import type { SelectedNetworkPath } from './streamRecovery/contracts';

export interface DecoderStatsObservation {
  sourceId: string;
  timestampMs: number;
  framesDecoded: number;
}

/** Values read out of one report. */
export type InboundRtpReading = {
  hasVideo: boolean;
  decodeSample?: DecoderStatsObservation;
  selectedPath?: SelectedNetworkPath;
  /** Selected video candidate-pair RTT, ms. Undefined when selection is unknown. */
  rttMs?: number;
  /** inbound-rtp jitter (RFC 3550, seconds); 0 when unreported, for the legacy quality stats. */
  jitter: number;
  /** The same figure, absent when the engine did not report it - the wire's omit-don't-zero copy. */
  jitterSeconds?: number;
  /** Cumulative inbound-rtp jitter-buffer counters (seconds / frames); only when emitted > 0. */
  jbDelayCumulative?: number;
  jbEmittedCumulative?: number;
  /** Cumulative jitterBufferTargetDelay / jitterBufferMinimumDelay (seconds), same divisor. */
  jbTargetDelayCumulative?: number;
  jbMinimumDelayCumulative?: number;
  /**
   * inbound-rtp framesPerSecond. Undefined when the engine did not report it — which must
   * stay distinct from a measured 0 (a stalled decoder). Zero-filling it would make an
   * engine that cannot measure framerate read at the host as a 0fps collapse, forever.
   */
  framesPerSecond?: number;
  packetsLost?: number;
  packetsReceived?: number;
  bytesReceived?: number;
  freezeCount?: number;
  totalFreezesDuration?: number;
  nackCount?: number;
  framesDecoded?: number;
  framesReceived?: number;
  /** inbound-rtp framesDropped (cumulative): received frames the engine discarded. */
  framesDropped?: number;
  totalDecodeTime?: number;
  totalInterFrameDelay?: number;
  /** inbound-rtp decoderImplementation, when the engine names it (e.g. "OMX.qcom..."). */
  decoderImplementation?: string;
  /** inbound-rtp powerEfficientDecoder: true on a hardware decoder, when reported. */
  powerEfficientDecoder?: boolean;
  /** inbound-rtp frameWidth / frameHeight of the last decoded frame. */
  frameWidth?: number;
  frameHeight?: number;
  /**
   * mimeType of the `codec` stat the video inbound-rtp's `codecId` points at, e.g.
   * "video/H264". Unlike decoderImplementation this is never permission-gated.
   */
  codecMimeType?: string;
  /** Audio inbound (observability-only): lifetime counters, differenced against the baseline. */
  audioBytesReceived?: number;
  audioPacketsLost?: number;
  audioPacketsReceived?: number;
  audioLevel?: number;
  /** Cumulative audio inbound-rtp jitter-buffer counters (seconds / frames); only when emitted > 0. */
  audioJbDelayCumulative?: number;
  audioJbEmittedCumulative?: number;
  audioJbTargetDelayCumulative?: number;
};

/** Read one report into its raw cumulative values. Pure; safe to call on any report shape. */
export function readInboundRtp(report: RTCStatsReport): InboundRtpReading {
  const reading: InboundRtpReading = { hasVideo: false, jitter: 0 };
  reading.selectedPath = new RtcSelectedPathReader().read(report);
  if (reading.selectedPath.rttMs !== undefined) reading.rttMs = reading.selectedPath.rttMs;
  // `codec` stats are keyed by id and referenced from inbound-rtp.codecId; collect them in the
  // same pass and resolve after, since report iteration order is not guaranteed.
  const codecMimeTypeById = new Map<string, string>();
  let videoCodecId: string | undefined;
  let videoCount = 0;

  report.forEach((stat) => {
    const entry = stat as Record<string, unknown>;
    if (entry['type'] === 'codec' && typeof entry['id'] === 'string' && typeof entry['mimeType'] === 'string') {
      codecMimeTypeById.set(entry['id'], entry['mimeType']);
    }
    if (entry['type'] !== 'inbound-rtp') return;

    // Audio inbound: observability-only. Read in the same pass; never an adaptation input.
    if (entry['kind'] === 'audio') {
      assignNumber(entry, 'bytesReceived', (v) => (reading.audioBytesReceived = v));
      assignNumber(entry, 'packetsLost', (v) => (reading.audioPacketsLost = v));
      assignNumber(entry, 'packetsReceived', (v) => (reading.audioPacketsReceived = v));
      assignNumber(entry, 'audioLevel', (v) => (reading.audioLevel = v));
      readAudioJitterBufferCounters(entry, reading);
      return;
    }

    if (entry['kind'] !== 'video') return;
    videoCount += 1;
    reading.hasVideo = true;
    reading.decodeSample = readDecoderObservation(entry);
    if (typeof entry['codecId'] === 'string') videoCodecId = entry['codecId'];
    readVideoCounters(entry, reading);
    readDecoderIdentity(entry, reading);
    readJitterBufferCounters(entry, reading);
  });

  // Ambiguous inbound streams cannot provide a single decoder observation.
  if (videoCount !== 1) reading.decodeSample = undefined;

  if (videoCodecId !== undefined) {
    const mimeType = codecMimeTypeById.get(videoCodecId);
    if (mimeType) reading.codecMimeType = mimeType;
  }
  return reading;
}

function readDecoderObservation(entry: Record<string, unknown>): DecoderStatsObservation | undefined {
  const id = entry['id'];
  const timestampMs = entry['timestamp'];
  const framesDecoded = entry['framesDecoded'];
  if (typeof id !== 'string' || !id || typeof timestampMs !== 'number' ||
      !Number.isFinite(timestampMs) || timestampMs < 0 || typeof framesDecoded !== 'number' ||
      !Number.isSafeInteger(framesDecoded) || framesDecoded < 0) return undefined;
  const ssrc = entry['ssrc'];
  if (ssrc !== undefined && (typeof ssrc !== 'number' || !Number.isSafeInteger(ssrc) || ssrc < 0)) return undefined;
  return { sourceId: JSON.stringify([id, ssrc ?? null]), timestampMs, framesDecoded };
}

function readVideoCounters(entry: Record<string, unknown>, reading: InboundRtpReading): void {
  assignNumber(entry, 'packetsLost', (v) => (reading.packetsLost = v));
  assignNumber(entry, 'packetsReceived', (v) => (reading.packetsReceived = v));
  assignNumber(entry, 'bytesReceived', (v) => (reading.bytesReceived = v));
  assignNumber(entry, 'freezeCount', (v) => (reading.freezeCount = v));
  assignNumber(entry, 'totalFreezesDuration', (v) => (reading.totalFreezesDuration = v));
  assignNumber(entry, 'nackCount', (v) => (reading.nackCount = v));
  assignNumber(entry, 'framesDecoded', (v) => (reading.framesDecoded = v));
  assignNumber(entry, 'framesReceived', (v) => (reading.framesReceived = v));
  assignNumber(entry, 'framesDropped', (v) => (reading.framesDropped = v));
  assignNumber(entry, 'totalDecodeTime', (v) => (reading.totalDecodeTime = v));
  assignNumber(entry, 'totalInterFrameDelay', (v) => (reading.totalInterFrameDelay = v));
  assignNumber(entry, 'jitter', (v) => {
    reading.jitter = v;
    reading.jitterSeconds = v;
  });
  assignNumber(entry, 'framesPerSecond', (v) => (reading.framesPerSecond = v));
  // Never treat inbound-rtp.timestamp (stats collection time) as a media RTP correlation
  // key. Calibrated latency uses rVFC rtpTimestamp via DecodeHintCollector.
}

function readDecoderIdentity(entry: Record<string, unknown>, reading: InboundRtpReading): void {
  const implementation = entry['decoderImplementation'];
  if (typeof implementation === 'string' && implementation.length > 0) {
    reading.decoderImplementation = implementation;
  }
  const powerEfficient = entry['powerEfficientDecoder'];
  if (typeof powerEfficient === 'boolean') reading.powerEfficientDecoder = powerEfficient;
  assignNumber(entry, 'frameWidth', (v) => (reading.frameWidth = v));
  assignNumber(entry, 'frameHeight', (v) => (reading.frameHeight = v));
}

/**
 * The three jitter-buffer sums share one divisor (`jitterBufferEmittedCount`), so all are
 * gated on it being positive: a zero divisor is "nothing emitted yet", not a measurement.
 */
function readJitterBufferCounters(entry: Record<string, unknown>, reading: InboundRtpReading): void {
  const delay = entry['jitterBufferDelay'];
  const emitted = entry['jitterBufferEmittedCount'];
  if (typeof delay !== 'number' || typeof emitted !== 'number' || emitted <= 0) return;
  reading.jbDelayCumulative = delay;
  reading.jbEmittedCumulative = emitted;
  assignNumber(entry, 'jitterBufferTargetDelay', (v) => (reading.jbTargetDelayCumulative = v));
  assignNumber(entry, 'jitterBufferMinimumDelay', (v) => (reading.jbMinimumDelayCumulative = v));
}

/**
 * The audio (NetEq) jitter buffer, same divisor rule as video. Observability-only: it exists
 * so an audio/video sync floor propping the video buffer up can be seen rather than inferred.
 */
function readAudioJitterBufferCounters(entry: Record<string, unknown>, reading: InboundRtpReading): void {
  const delay = entry['jitterBufferDelay'];
  const emitted = entry['jitterBufferEmittedCount'];
  if (typeof delay !== 'number' || typeof emitted !== 'number' || emitted <= 0) return;
  reading.audioJbDelayCumulative = delay;
  reading.audioJbEmittedCumulative = emitted;
  assignNumber(entry, 'jitterBufferTargetDelay', (v) => (reading.audioJbTargetDelayCumulative = v));
}

function assignNumber(
  entry: Record<string, unknown>,
  key: string,
  assign: (value: number) => void,
): void {
  const value = entry[key];
  if (typeof value === 'number' && Number.isFinite(value)) assign(value);
}
